import { Component } from "@angular/core";
import { UntypedFormControl, Validators } from "@angular/forms";
import { GraphApplication } from "src/app/models/graph/applications/GraphApplication";
import { GraphApplicationService } from "src/app/services/graph/GraphApplication.service";
import { PageLayout } from '@cvx/nextpage';
import { IGraphApplicationPermissions } from "src/app/models/graph/applications/GraphApplicationPermissions";
import { GraphAppPermissionRequestModel } from 'src/app/models/graph/applications/GraphAppPermissionRequest';
import { GraphConstants } from "src/app/models/graph/Graph.constants";
import { IGenericApiResponseWithWorkflowRequest } from "src/app/models/common/GenericApiResponseWithWorkflowRequest";
import { ErrorMessageConstants } from "src/app/constants/ErrorMessage.constants";
import { environment } from "src/environments/environment";
import { LpEvaluatorService } from "src/app/services/lp-evaluator/lp-evaluator.service";
import { LpEvaluationState, LpDecisionResult } from "src/app/models/lp-evaluation/LpEvaluationResult";

@Component({
    selector: 'request-permissions-application',
    templateUrl: './request-permissions.html',
    styleUrls: ['./request-permissions.css']
})

export class RequestPermissionsComponent {


    showUpdateApplication: boolean = false;
    PageLayout = PageLayout;
    submitErrorMessage = { message: '', errors: [] };
    isLoading: boolean = false;
    appSearchInProgress: boolean = true;
    isCreating: boolean = false;
    isCompleted: boolean = false;
    panelOpenState = false;
    createdResponse: IGenericApiResponseWithWorkflowRequest<any>;
    searchFoundNoExposedPermissions: boolean = false;

    //setup toggle tabs
    readonly allowClientAppTypeSelection: boolean = environment.isManagedIdentityPermissionRequestCapabilitiesEnabled;
    readonly appReg: string = 'App Reg';
    readonly managedIdentity: string = 'Managed Identity';
    toggleTabs: string[] = [this.appReg, this.managedIdentity];
    title: string = 'client application type';
    options: string[] = this.toggleTabs;
    initialSelection: string = this.appReg;
    clientAppType: string = this.initialSelection;

    ownedApps: GraphApplication[] = [];

    /** LP evaluation state — only active when Microsoft Graph is the resource app */
    lpState: LpEvaluationState = { status: 'idle', result: null, errorMessage: null };

    /** Minimum justification length (chars) before the evaluate button becomes visible */
    readonly LP_EVAL_MIN_CHARS = 50;

    constructor(
        private graphApplicationService: GraphApplicationService,
        private lpEvaluatorService: LpEvaluatorService,
    ) { }


    // ── Getters ───────────────────────────────────────────────────────────────

    get isGraphResourceApp(): boolean {
        if (!this.resourceApp) return false;
        const app = this.resourceApp.application;
        const result = this.lpEvaluatorService.isMicrosoftGraph(app);
        if (!result) {
            console.debug('[LP] isGraphResourceApp=false  appId=%s  displayName=%s', app?.appId, app?.displayName);
        }
        return result;
    }

    get canEvaluate(): boolean {
        return this.isGraphResourceApp
            && (this.resourceApp?.permissions?.length ?? 0) > 0
            && (this.justification.value?.trim().length ?? 0) >= this.LP_EVAL_MIN_CHARS
            && this.lpState.status !== 'evaluating';
    }

    /** AI review is only offered for Microsoft Graph resource apps — nothing to require otherwise. */
    get requiresLpEvaluation(): boolean {
        return this.isGraphResourceApp;
    }

    /** True once an evaluation attempt has finished, regardless of outcome (agree/clarify/blocked/error). */
    get hasAttemptedEvaluation(): boolean {
        return this.lpState.status === 'complete' || this.lpState.status === 'blocked' || this.lpState.status === 'error';
    }

    get canSubmit(): boolean {
        return this.clientApp !== undefined
            && this.resourceApp !== undefined
            && this.justification.valid
            && (!this.requiresLpEvaluation || this.hasAttemptedEvaluation);
    }

    get lpLegendClass(): string {
        switch (this.lpState.status) {
            case 'evaluating': return 'bg-secondary text-white';
            case 'blocked':
            case 'error':      return 'bg-danger text-white';
            case 'complete':
                switch (this.lpState.result?.decisionType) {
                    case 'Agree':             return 'bg-success text-white';
                    case 'Clarify':           return 'bg-warning text-dark';
                    case 'RecommendDifferent': return 'lp-legend-recommend text-white';
                }
        }
        return 'bg-dark-blue text-white';
    }

    get lpBadgeLabel(): string {
        if (this.lpState.status === 'blocked') return 'blocked';
        if (this.lpState.status === 'error')   return 'error';
        switch (this.lpState.result?.decisionType) {
            case 'Agree':              return '✓ approved as requested';
            case 'Clarify':            return '⚠ clarification needed';
            case 'RecommendDifferent': return '↕ different permissions suggested';
        }
        return '';
    }

    clientApp?: GraphApplication;
    resourceApp?: IGraphApplicationPermissions;

    displayClientApp: any;
    displayResourceApp: any;

    justification = new UntypedFormControl('', { validators: [Validators.required] });

    handleToggleChange(selectedOption: string) {
        this.clientAppType = selectedOption;
        if (this.clientAppType === this.managedIdentity) {
            this.resetResourceAppSelected();
        }
    }
    searchStatusChanged(searchInProgress: boolean) {
        this.appSearchInProgress = searchInProgress;
    }

    clientAppSelected(data: IGraphApplicationPermissions) {
        this.clientApp = data.application;
        this.showUpdateApplication = Number.isNaN(parseInt(this.clientApp.serviceId))

        if (this.clientAppType === this.managedIdentity) {
            this.displayClientApp = {
                id: data.application.id,
                appId: data.application.appId,
                type: "Managed Identity",
                name: data.application.displayName,
                serviceId: this.showUpdateApplication ? ErrorMessageConstants.MissingData : data.application.serviceId,
                uri: data.application?.identifierUris?.length > 0 ? data.application.identifierUris[0] : '',
            };
        }
        else {
            this.displayClientApp = {
                id: data.application.id,
                appId: data.application.appId,
                name: data.application.displayName,
                serviceId: this.showUpdateApplication ? ErrorMessageConstants.MissingData : data.application.serviceId,
                uri: data.application?.identifierUris?.length > 0 ? data.application.identifierUris[0] : '',
            };
        }
    }

    resetClientAppSelected() {
        this.clientApp = undefined;
        this.displayClientApp = undefined;
    }

    resetResourceAppSelected() {
        this.resourceApp = undefined;
        this.displayResourceApp = undefined;
        this.lpState = { status: 'idle', result: null, errorMessage: null };
    }

    updatedApplications(data: GraphApplication[]) {
        this.displayClientApp.serviceId = data[0].serviceId;
        this.displayClientApp = { ...this.displayClientApp };
        this.clientApp = data[0];
        this.showUpdateApplication = false;
    }
    resourceAppSelected(data: IGraphApplicationPermissions) {
        this.resourceApp = data;
        if (data.application.id) {
            this.displayResourceApp = {
                id: data.application.id,
                appId: data.application.appId,
                name: data.application.displayName,
                uri: data.application.identifierUris[0],
                owners: data.application.owners.map(item => item.displayName),
                permissions: data.permissions.map(item => {
                    let type = GraphConstants.AppPermissionsDisplayValues.get(item.type) ?? 'unknown';
                    return `${item.value} (${type})`;
                })
            };
        }
        else {
            this.displayResourceApp = {
                name: data.application.displayName,
                permissions: data.permissions.map(item => {
                    let type = GraphConstants.AppPermissionsDisplayValues.get(item.type) ?? 'unknown';
                    return `${item.value} (${type})`;
                })
            }
        }
    }

    async onSubmit() {

        // clear error messages
        this.submitErrorMessage.message = '';
        this.submitErrorMessage.errors = [];

        if (this.clientApp !== undefined && this.resourceApp !== undefined && this.justification.valid
            && (!this.requiresLpEvaluation || this.hasAttemptedEvaluation)) {
            let clientId = this.clientApp.id;
            let payload = new GraphAppPermissionRequestModel(
                this.resourceApp.application.servicePrincipalId,
                this.resourceApp.permissions.filter(item => item.type === GraphConstants.PermissionsScope).map(item => item.id),
                this.resourceApp.permissions.filter(item => item.type === GraphConstants.PermissionsApp).map(item => item.id),
                this.justification.value,
                this.clientAppType === this.managedIdentity
            );

            // Attach AI evaluation result so the approver can see it in the request details
            payload.lpEvaluationResult = this.lpState.status === 'complete' ? this.lpState.result : null;

            const observer = {
                next: (x: IGenericApiResponseWithWorkflowRequest<any>) => {
                    this.createdResponse = x;
                },
                error: (err: any) => {
                    this.submitErrorMessage.message = err.statusText;
                    this.submitErrorMessage.errors = err?.error?.errors ?? [];
                    this.isCreating = false;
                },
                complete: () => {
                    this.isCreating = false;
                    this.isCompleted = true;
                }
            };

            this.isCreating = true;
            let createCall = this.graphApplicationService.CreateApplicationPermissionRequest(clientId, payload);
            createCall.subscribe(observer);
        }
        else if (this.justification.invalid) {
            this.submitErrorMessage.message = 'a business justification is required';
        }
        else if (this.requiresLpEvaluation && !this.hasAttemptedEvaluation) {
            this.submitErrorMessage.message = 'Please evaluate your justification with the AI reviewer before submitting.';
        }
    }

    noExposedPermissionsFound(event: boolean) {
        this.searchFoundNoExposedPermissions = event;
    }

    async evaluatePermissions() {
        if (!this.canEvaluate || !this.resourceApp) return;

        this.lpState = { status: 'evaluating', result: null, errorMessage: null };

        try {
            const result: LpDecisionResult = await this.lpEvaluatorService.evaluate(
                this.resourceApp.permissions,
                this.justification.value ?? '',
            );
            this.lpState = { status: result.decisionType === 'Clarify' && result.explanation.includes('Content safety') ? 'blocked' : 'complete', result, errorMessage: null };
        } catch (err: unknown) {
            const message = (err as Error)?.message ?? 'An unexpected error occurred during evaluation.';
            this.lpState = { status: 'error', result: null, errorMessage: message };
        }
    }

    resetForm() {
        this.clientApp = undefined;
        this.displayClientApp = undefined;
        this.resourceApp = undefined;
        this.displayResourceApp = undefined;
        this.justification.setValue('');
        this.lpState = { status: 'idle', result: null, errorMessage: null };
    }

}